import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { CheckCircle2, Circle, Calendar } from "lucide-react";

export default function ExhibitorFollowups() {
  const [tasks, setTasks] = useState(null);
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => { const { data } = await api.get("/exhibitor/followups"); setTasks(data); };
  useEffect(() => { load(); }, []);

  const complete = async (id) => { await api.put(`/exhibitor/followups/${id}`); toast.success("Marked done"); load(); };

  const testDigest = async () => {
    try {
      const { data } = await api.post("/exhibitor/notifications/test-digest?force=true");
      toast.success(`Demo: ${data.digest_notifications} follow-up digest + ${data.summary_notifications} summary notification(s) generated`);
    } catch { toast.error("Failed to generate digest"); }
  };

  const pending = (tasks || []).filter((t) => t.status === "pending");
  const done = (tasks || []).filter((t) => t.status === "done");

  const Row = ({ t }) => (
    <div className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`followup-${t.id}`}>
      <button onClick={() => t.status !== "done" && complete(t.id)} data-testid={`followup-toggle-${t.id}`}>
        {t.status === "done" ? <CheckCircle2 size={22} className="text-amber-400" /> : <Circle size={22} className="text-slate-500" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${t.status === "done" ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</div>
        <button onClick={() => navigate(`/exhibitor/leads/${t.lead_id}`)} className="text-xs text-amber-300/80">{t.lead_name}</button>
        {t.assigned_to && <span className="text-[10px] text-slate-500 ml-2">· {t.assigned_to}</span>}
      </div>
      {t.due_date && (
        <div className={`flex items-center gap-1 text-xs ${t.status !== "done" && t.due_date < today ? "text-red-300 font-semibold" : t.status !== "done" && t.due_date === today ? "text-amber-300 font-semibold" : "text-slate-400"}`} data-testid={`followup-due-${t.id}`}>
          <Calendar size={12} /> {t.due_date}{t.status !== "done" && t.due_date < today ? " · overdue" : t.status !== "done" && t.due_date === today ? " · today" : ""}
        </div>
      )}
    </div>
  );

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Reminders</Overline>
      <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
        <h1 className="font-display text-3xl gold-text">Follow-up Tasks</h1>
        <button onClick={testDigest} className="glass px-4 py-2 rounded-xl text-xs text-slate-300 hover:text-amber-300 border border-amber-500/20" data-testid="test-digest-button">Send test digest (demo)</button>
      </div>

      {!tasks ? <Spinner /> : tasks.length === 0 ? (
        <p className="text-slate-400 text-center py-16">No tasks yet. Create follow-ups from a lead's page.</p>
      ) : (
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-sub text-sm uppercase tracking-wider text-slate-300 mb-3">Pending ({pending.length})</h3>
            <div className="space-y-3">{pending.length ? pending.map((t) => <Row key={t.id} t={t} />) : <p className="text-slate-500 text-sm">All caught up ✨</p>}</div>
          </div>
          {done.length > 0 && (
            <div>
              <h3 className="font-sub text-sm uppercase tracking-wider text-slate-300 mb-3">Completed ({done.length})</h3>
              <div className="space-y-3">{done.map((t) => <Row key={t.id} t={t} />)}</div>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}
